import { useState, useEffect } from 'react';
import { Redirect } from 'react-router';
import * as Sentry from '@sentry/react';
import AuthenticationContext from './Context';
import Loader from 'components/Loader';
import Okta from 'providers/Okta';
import Tokens from 'providers/Tokens';
import validateFromURI, { validateInnerRoute } from 'utilities/validateFromURI';
import getStorage from 'utilities/getStorage';
import Logger from 'utilities/logger';

const Provider = props => {
  const { children } = props;
  const storage = getStorage();
  const logger = Logger('authentication');
  const [state, setState] = useState({
    permissions: [],
    loading: true,
    login: false,
    user: null,
  });
  const auth = Okta();
  const update = data => setState(old => ({ ...old, ...data }));

  const refresh = async () => {
    auth.client.tokenManager.remove('idToken');
    const response = await auth.idToken();
    const {
      claims: {
        mycncScopes: permissions = [],
        family_name: lastName,
        given_name: firstName,
        preferred_username: email,
        sub: uid,
        terms_group: terms,
        mpn_group: mpn,
      } = {},
    } = response || {};
    const idp = await auth.isIDP(email);
    const groups = { terms, mpn };
    const user = { uid, firstName, lastName, email, groups, idp };
    Sentry.setUser({ firstName, lastName, email, id: uid });
    update({ permissions, user });
    return { user, permissions };
  };

  const requestToken = async scope => {
    const token = await Tokens.request(auth, scope);
    return token;
  };

  const logout = async () => {
    update({
      permissions: [],
      loading: true,
      login: false,
      user: null,
    });
    logger.info('Logged out');
    Tokens.clear();
    await auth.logout();
  };

  useEffect(() => {
    update({ loading: true });
    logger.debug('Checking session');

    if (auth.client.token.isLoginRedirect()) {
      // this would be an ugly hack until parseFromUrl() works properly
      // response = await okta.token.parseFromUrl();
      logger.debug('Is Login Redirect');
      window.location.replace('/');
      return;
    }
      auth
        .checkSession()
        .then(session => {
          update({ login: session });
          logger.debug({ session });
          if (!session) {
            update({ loading: false });
            return <Redirect to="/login" />;
          }
          refresh().then(response => {
            logger.debug(response);
            logger.info('success');
            update({ loading: false });

            const redirect = storage.getItem('redirect');
            storage.removeItem('redirect');
            if (redirect && validateFromURI(redirect)) {
              logger.debug('Redirecting fromURI');
              window.location.assign(redirect);
            } else if (redirect && validateInnerRoute(redirect)) {
              logger.debug('Redirecting inner route');
              window.location.replace(redirect);
            }
          })
          .catch(err=> {
            logger.error(err);
            update({
              permissions: [],
              loading: false,
              login: false,
              user: null,
            });
          })
        })
        .catch(err => {
          logger.error(err);
          update({
            permissions: [],
            loading: false,
            login: false,
            user: null,
          });
        });
  }, []);

  if (state.loading) {
    return <Loader message="Checking Authentication Status" />;
  }

  const value = { ...state, auth, storage, logout, refresh, requestToken };

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export default Provider;
